import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { withApiError } from "../../../components/ReactHookForm/ApiError";
import { CUSTOMERS_DOCUMENTS_STATES_VALUE_ACCEPTED } from "../../../constants/customersDocuments";
import { POLICIES_POLICY_VALUE_ACCEPT } from "../../../constants/policies";
import useCanPerform from "../../../hooks/useCanPerform";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

const CustomersDocumentsActionAccept = withApiError(
  /**
   * @typedef {object} Props
   * @property {import("../../../types/CustomerDocument").CustomerDocument} [customerDocument] associated customer document
   * @property {() => void} mutate
   * @property {(params: {isAllowed: boolean, trigger: () => void}) => import("react").ReactElement | null} render
   *
   * @param {Props} props
   */
  function CustomersDocumentsActionAccept({
    customerDocument,
    mutate,
    render,
  }) {
    const intl = useIntl();

    const { isOpen, onOpen, onClose } = useDisclosure();

    const canperform = useCanPerform();

    const buttonRef = useRef(null);

    const { request, toastSuccess } = useRequest();

    const handleClickButton = useCallback(() => {
      request(
        generateApiUri({
          id: "@customers.documents.update",
          parameters: { documentId: customerDocument?.id },
        }),
        {
          method: "post",
          body: {
            data: {
              state: CUSTOMERS_DOCUMENTS_STATES_VALUE_ACCEPTED,
            },
          },
        },
      )
        .then(() => {
          onClose();
          mutate();
          toastSuccess({
            title: intl.formatMessage({ defaultMessage: "Document validé" }),
          });
        })
        .catch((response) => {});
    }, [customerDocument?.id, intl, mutate, onClose, request, toastSuccess]);

    return (
      <>
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          initialFocusRef={buttonRef}
          isCentered>
          <ModalOverlay />

          <ModalContent>
            <ModalHeader>
              {intl.formatMessage({ defaultMessage: "Validation du document" })}
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody>
              <Text>
                {intl.formatMessage({
                  defaultMessage:
                    "Êtes-vous sûr de vouloir valider ce document ?",
                })}
              </Text>
            </ModalBody>

            <ModalFooter>
              <Wrap>
                <Button onClick={onClose} variant="outline" colorScheme="gray">
                  {intl.formatMessage({ defaultMessage: "Annuler" })}
                </Button>

                <Button
                  ref={buttonRef}
                  onClick={handleClickButton}
                  colorScheme="brandSecondary">
                  {intl.formatMessage({ defaultMessage: "Valider" })}
                </Button>
              </Wrap>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {render({
          isAllowed: canperform({
            policies: POLICIES_POLICY_VALUE_ACCEPT,
            resourcePolicies: customerDocument?.meta?.policies,
          }),
          trigger: onOpen,
        })}
      </>
    );
  },
);

export default CustomersDocumentsActionAccept;
